import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";

import { getEventCoordinates } from "@/shared/utils/dom";
import { selectorSetting } from "@/shared/store/settings/selectors";
import { SETTING } from "@/shared/utils/settings/constants";
import { isDarkTheme } from "@/shared/utils/theme/theme";

import styles from "./Ripple.module.scss";

const Ripple = ({ activateOnMove, idRipple, className }) => {
  const themeMode = useSelector(selectorSetting(SETTING.themeMode.id));
  const [darkTheme, setDarkTheme] = useState(false);

  const [released, setReleased] = useState(false);
  const [pressedTimeStamp, setPressedTimestamp] = useState(false);
  const [rippleStyle, setRippleStyle] = useState(null);

  const updateRippleStyleByEvent = (e) => {
    const currentTargetRect = e.currentTarget.getBoundingClientRect();

    const coords = getEventCoordinates(e);

    setRippleStyle({
      left: coords.x - currentTargetRect.x,
      top: coords.y - currentTargetRect.y,
    });
  };

  const handleStart = (e) => {
    if (!pressedTimeStamp) {
      setPressedTimestamp(e.timeStamp);
    }

    updateRippleStyleByEvent(e);
  };

  const handleMove = (activateOnMove || pressedTimeStamp)
    ? activateOnMove
      ? handleStart
      : updateRippleStyleByEvent
    : undefined;

  const handleLeave = activateOnMove ? () => {
    setPressedTimestamp(null);
  } : undefined;

  // Listen for end interaction event.
  useEffect(() => {
    const animationPressedMS = 200;
    const animationReleasedMS = 200;

    let timerIdAnimationPressed, timerIdAnimationRelease;

    const onTimerReleasedFire = () => {
      setReleased(null);
    };

    const onTimerPressedFire = () => {
      setPressedTimestamp(null);

      setReleased(true);

      timerIdAnimationRelease = setTimeout(onTimerReleasedFire, animationReleasedMS);
    };

    const listener = (e) => {
      const delta = e.timeStamp - pressedTimeStamp;

      if (delta > animationPressedMS) {
        onTimerPressedFire();
      } else {
        timerIdAnimationPressed = setTimeout(onTimerPressedFire, animationPressedMS - delta);
      }
    };

    if (pressedTimeStamp) {
      // uses capture, because an event may be propagation stopped.
      document.addEventListener("mouseup", listener, true);
      document.addEventListener("touchend", listener, true);
    }

    return () => {
      if (timerIdAnimationRelease) {
        clearTimeout(timerIdAnimationRelease);
        onTimerReleasedFire();
      }

      if (timerIdAnimationPressed) {
        clearTimeout(timerIdAnimationPressed);
        onTimerPressedFire();
      }

      if (pressedTimeStamp) {
        document.removeEventListener("mouseup", listener, true);
        document.removeEventListener("touchend", listener, true);
      }
    };
  }, [pressedTimeStamp]);

  // Fixes hydration error: Prop `className` did not match.
  useEffect(() => {
    setDarkTheme(isDarkTheme(themeMode));
  }, [themeMode]);

  return (
    <div
      id={idRipple ?? ""}
      className={classNames(styles["root"], className, {
        [styles["dark"]]: !darkTheme,
      })}

      onTouchStart={handleStart}
      onMouseDown={handleStart}

      onMouseMove={handleMove}
      onTouchMove={handleMove}

      onMouseLeave={handleLeave}
      onBlur={handleLeave}
    >
      <span
        className={classNames(styles["ripple"], {
          [styles["pressed"]]: pressedTimeStamp,
          [styles["released"]]: released,
        })}
        style={rippleStyle}
      />
    </div>
  );
};

export default Ripple;
